import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { displayToastInfo } from '@/helpers/display-toast';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { SettingsRoutes } from '@/router/routes';
import { logger } from '@/utils/logger/logger';

import { NOTIFICATION_PROMPT_OPTED_OUT_STORAGE_KEY } from '../constants/notification-storage-keys';
import notificationService from '../firebase-notification.service';
import { NotificationPermissionState } from '../types/notification-permission-state';

const TAG = 'useNotificationPermission:';

const NOTIFICATION_DENIED_REMINDED_AT_STORAGE_KEY = 'notification_denied_reminded_at';

const REMINDER_HOUR = 9;
const DENIED_REMINDER_INTERVAL_DAYS = 7;
const OPT_OUT_TOAST_DURATION_MS = 8_000;

const shouldShowDeniedReminder = (remindedAt: string | null): boolean => {
  if (!remindedAt) return true;

  const remindedDate = new Date(remindedAt);
  if (isNaN(remindedDate.getTime())) return true;

  const nextReminder = new Date(remindedDate);
  nextReminder.setDate(nextReminder.getDate() + DENIED_REMINDER_INTERVAL_DAYS);
  nextReminder.setHours(REMINDER_HOUR, 0, 0, 0);

  return new Date() >= nextReminder;
};

export const useNotificationPermission = () => {
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showDeniedModal, setShowDeniedModal] = useState(false);
  const { t } = useTranslation('authenticated.notifications');
  const navigate = useNavigate();
  const [isOptedOut, setIsOptedOut] = useLocalStorage<boolean>(
    NOTIFICATION_PROMPT_OPTED_OUT_STORAGE_KEY,
    false
  );
  const [deniedRemindedAt, setDeniedRemindedAt] = useLocalStorage<string | null>(
    NOTIFICATION_DENIED_REMINDED_AT_STORAGE_KEY,
    null
  );

  const initializeNotifications = useCallback(async () => {
    try {
      logger.debug(TAG, 'Initializing Firebase notifications');
      await notificationService.requestPermissionAndInitialize();
      logger.info(TAG, 'Firebase notifications initialized successfully');
    } catch (error) {
      logger.error(TAG, 'Failed to initialize Firebase notifications', error);
    }
  }, []);

  useEffect(() => {
    if (isOptedOut) return;

    const permissionState = notificationService.checkPermissionState();

    if (permissionState === NotificationPermissionState.PROCEED) {
      initializeNotifications();
      return;
    }

    if (
      permissionState === NotificationPermissionState.DENIED &&
      shouldShowDeniedReminder(deniedRemindedAt)
    ) {
      setShowDeniedModal(true);
      return;
    }

    if (permissionState === NotificationPermissionState.PROMPT) {
      setShowPermissionModal(true);
    }
  }, [initializeNotifications, isOptedOut, deniedRemindedAt]);

  const handleEnableNotifications = useCallback(async () => {
    setShowPermissionModal(false);
    await initializeNotifications();

    // User clicked "Enable" but denied the browser prompt
    if (Notification.permission === 'denied') {
      setShowDeniedModal(true);
    }
  }, [initializeNotifications]);

  const handleDismissPermissionModal = useCallback(() => {
    setShowPermissionModal(false);
    setIsOptedOut(true);
    displayToastInfo(t('optOutToast.title'), {
      description: t('optOutToast.description'),
      duration: OPT_OUT_TOAST_DURATION_MS,
      action: {
        label: t('optOutToast.action'),
        onClick: () => navigate(SettingsRoutes.Notifications),
      },
    });
  }, [setIsOptedOut, t, navigate]);

  const handleDismissDeniedModal = useCallback(() => {
    setShowDeniedModal(false);
    setDeniedRemindedAt(new Date().toISOString());
  }, [setDeniedRemindedAt]);

  return {
    showPermissionModal,
    showDeniedModal,
    handleEnableNotifications,
    handleDismissPermissionModal,
    handleDismissDeniedModal,
  };
};
